import React, { useEffect, useState } from "react";
import { Loader2, Save, ExternalLink, ShoppingBag, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { useLocation } from "react-router-dom";

const PROVIDERS = [
  { value: "skedda", label: "Skedda (external new tab)", desc: "Click on any Book Lane CTA opens Skedda in a new tab. Recommended for go-live." },
  { value: "iframe", label: "Skedda (embedded iframe)", desc: "Loads Skedda inside the /book page on this domain. ⚠️ Skedda currently sends X-Frame-Options: sameorigin, so the embed will appear blank in production — use only if Skedda allow-lists this domain.", warn: true },
  { value: "inhouse", label: "In-house booking flow", desc: "Use the legacy multi-step in-house booking page." },
];

const AdminSettings = () => {
  const { refresh: refreshSettings } = useSettings();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopify, setShopify] = useState(null);
  const [shopifyBusy, setShopifyBusy] = useState(false);

  const loadShopify = async () => {
    try {
      const { data } = await api.get("/admin/shopify/status", { withCredentials: true });
      setShopify(data);
    } catch {
      setShopify(null);
    }
  };

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/admin/settings", { withCredentials: true });
      setData(data);
    } catch {
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
    await loadShopify();
  })(); }, []);

  // Toast any Shopify callback status that arrived via query param
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const s = p.get("shopify");
    if (!s) return;
    if (s === "connected") toast.success("Shopify connected successfully");
    else toast.error(`Shopify connect failed: ${s.replaceAll("_", " ")}`);
  }, [location.search]);

  const connectShopify = async () => {
    setShopifyBusy(true);
    try {
      const { data } = await api.get("/admin/shopify/install-url", { withCredentials: true });
      window.location.href = data.install_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate install URL");
      setShopifyBusy(false);
    }
  };

  const disconnectShopify = async () => {
    if (!window.confirm("Disconnect the Shopify integration? New signups will stop syncing.")) return;
    try {
      await api.post("/admin/shopify/disconnect", {}, { withCredentials: true });
      toast.success("Shopify disconnected");
      await loadShopify();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        booking_provider: data.booking_provider,
        booking_skedda_url: data.booking_skedda_url,
        show_membership: !!data.show_membership,
        show_inhouse_booking: !!data.show_inhouse_booking,
        show_shop: !!data.show_shop,
        shop_iframe_url: data.shop_iframe_url || "",
      }, { withCredentials: true });
      toast.success("Settings saved");
      await refreshSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <div className="flex items-center gap-2 text-spike-dim p-6"><Loader2 className="w-4 h-4 animate-spin"/> Loading...</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings-page">
      <div>
        <h1 className="font-orbitron text-2xl font-black uppercase text-spike-text">Site Settings</h1>
        <p className="text-sm text-spike-text-muted mt-1">Global toggles that affect the public site.</p>
      </div>

      <section className="bg-spike-elevated border border-white/10 rounded-lg p-5 space-y-4">
        <h2 className="font-orbitron text-sm font-bold uppercase tracking-[0.2em] text-spike-blue-light">Booking Provider</h2>
        <div className="space-y-2">
          {PROVIDERS.map((p) => (
            <label key={p.value} className={`flex gap-3 p-3 rounded-md border cursor-pointer ${data.booking_provider === p.value ? "border-spike-blue bg-spike-blue/5" : "border-white/10 bg-black/40"}`} data-testid={`booking-provider-${p.value}`}>
              <input
                type="radio"
                name="booking_provider"
                value={p.value}
                checked={data.booking_provider === p.value}
                onChange={() => setData({ ...data, booking_provider: p.value })}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-semibold text-spike-text">{p.label}</div>
                <div className={`text-xs mt-1 ${p.warn ? "text-amber-300" : "text-spike-text-muted"}`}>{p.desc}</div>
              </div>
            </label>
          ))}
        </div>

        <div>
          <label className="text-xs font-orbitron uppercase tracking-wider text-spike-dim">Skedda URL</label>
          <input
            type="url"
            value={data.booking_skedda_url || ""}
            onChange={(e) => setData({ ...data, booking_skedda_url: e.target.value })}
            data-testid="booking-skedda-url-input"
            className="w-full mt-1 bg-black/60 border border-white/15 rounded-md px-3 py-2 text-sm text-spike-text focus:outline-none focus:border-spike-blue"
          />
          <a href={data.booking_skedda_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-spike-blue-light hover:underline">
            Open Skedda <ExternalLink className="w-3 h-3"/>
          </a>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-settings-save-btn"
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save Settings
        </button>
      </section>

      <section className="bg-spike-elevated border border-white/10 rounded-lg p-5 space-y-4" data-testid="feature-flags-section">
        <h2 className="font-orbitron text-sm font-bold uppercase tracking-[0.2em] text-spike-blue-light">Feature Flags</h2>
        <p className="text-xs text-spike-text-muted">Disable parts of the site that are not ready for go-live. Pages stay protected behind the flag — direct URL visits return 404.</p>

        <label className="flex items-start gap-3 p-3 rounded-md border border-white/10 bg-black/40 cursor-pointer" data-testid="flag-show-membership-row">
          <input
            type="checkbox"
            data-testid="flag-show-membership"
            checked={!!data.show_membership}
            onChange={(e) => setData({ ...data, show_membership: e.target.checked })}
            className="mt-1"
          />
          <div>
            <div className="text-sm font-semibold text-spike-text">Show Membership page</div>
            <div className="text-xs text-spike-text-muted mt-1">When off, /membership is hidden from the header, footer and home page, and direct visits return 404. Turn back on after payments are wired.</div>
          </div>
        </label>

        <label className="flex items-start gap-3 p-3 rounded-md border border-white/10 bg-black/40 cursor-pointer" data-testid="flag-show-inhouse-booking-row">
          <input
            type="checkbox"
            data-testid="flag-show-inhouse-booking"
            checked={!!data.show_inhouse_booking}
            onChange={(e) => setData({ ...data, show_inhouse_booking: e.target.checked })}
            className="mt-1"
          />
          <div>
            <div className="text-sm font-semibold text-spike-text">Enable in-house booking flow</div>
            <div className="text-xs text-spike-text-muted mt-1">When off, even selecting &quot;In-house&quot; above falls back to the Skedda redirect — protects you from accidentally exposing the legacy flow before payments are integrated.</div>
          </div>
        </label>

        <div className="p-3 rounded-md border border-white/10 bg-black/40 space-y-3" data-testid="flag-show-shop-row">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="flag-show-shop"
              checked={!!data.show_shop}
              onChange={(e) => setData({ ...data, show_shop: e.target.checked })}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-semibold text-spike-text">Show Pro Shop on home page</div>
              <div className="text-xs text-spike-text-muted mt-1">When off, the Cricket Pro Shop section is hidden from the home page and footer. When on, it shows either the Shopify iframe (if a URL is set below) or the curated teaser cards.</div>
            </div>
          </label>
          <div>
            <label className="text-xs font-orbitron uppercase tracking-wider text-spike-dim">Shopify embed URL</label>
            <input
              type="url"
              value={data.shop_iframe_url || ""}
              onChange={(e) => setData({ ...data, shop_iframe_url: e.target.value })}
              data-testid="shop-iframe-url-input"
              placeholder="https://yourshop.myshopify.com/"
              className="w-full mt-1 bg-black/60 border border-white/15 rounded-md px-3 py-2 text-sm text-spike-text focus:outline-none focus:border-spike-blue"
            />
            <p className="text-[0.65rem] text-spike-dim mt-1 font-exo">Tip: most Shopify themes send X-Frame-Options. Use a dedicated embeddable collection URL or a Buy Button embed if the main storefront refuses to load in an iframe.</p>
          </div>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-settings-flags-save-btn"
          className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save Flags
        </button>
      </section>

      <section className="bg-spike-elevated border border-white/10 rounded-lg p-5 space-y-4" data-testid="shopify-integration-section">
        <div className="flex items-center justify-between">
          <h2 className="font-orbitron text-sm font-bold uppercase tracking-[0.2em] text-spike-blue-light inline-flex items-center gap-2">
            <ShoppingBag className="w-4 h-4"/> Shopify Integration
          </h2>
          <button type="button" onClick={loadShopify} className="text-xs text-spike-dim hover:text-spike-text inline-flex items-center gap-1" data-testid="shopify-refresh-btn">
            <RefreshCw className="w-3 h-3"/> Refresh
          </button>
        </div>
        <p className="text-xs text-spike-text-muted">
          Sync every new account into your Shopify customer list, and let Shopify send the welcome / account-invite email automatically.
        </p>

        {!shopify?.configured ? (
          <div className="flex items-start gap-2 p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs" data-testid="shopify-not-configured">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5"/>
            <div>
              Shopify env vars are not configured. Ask your admin to set <code className="font-mono">SHOPIFY_SHOP_DOMAIN</code>, <code className="font-mono">SHOPIFY_CLIENT_ID</code>, <code className="font-mono">SHOPIFY_CLIENT_SECRET</code> and <code className="font-mono">SHOPIFY_REDIRECT_URI</code> in <code>backend/.env</code>.
            </div>
          </div>
        ) : !shopify?.installed ? (
          <div data-testid="shopify-needs-install">
            <p className="text-sm text-spike-text font-exo">
              Configured for shop <span className="font-mono text-spike-blue-light">{shopify.shop}</span> — not yet installed.
            </p>
            <button
              type="button"
              data-testid="shopify-connect-btn"
              disabled={shopifyBusy}
              onClick={connectShopify}
              className="mt-3 inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm"
            >
              {shopifyBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>} Connect Shopify
            </button>
            <p className="mt-2 text-[0.65rem] text-spike-dim font-exo">
              You&apos;ll be sent to Shopify to approve the install, then returned here.
            </p>
          </div>
        ) : (
          <div data-testid="shopify-installed">
            <div className="flex items-center gap-2 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm">
              <CheckCircle2 className="w-4 h-4"/>
              <div className="flex-1">
                Connected to <span className="font-mono">{shopify.shop}</span>
                {shopify.installed_at && <> · since {new Date(shopify.installed_at).toLocaleDateString("en-CA")}</>}
              </div>
              <span className="text-xs text-emerald-200/80">{shopify.synced_users} synced</span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div className="bg-black/40 border border-white/10 rounded-md p-3">
                <div className="text-spike-dim uppercase font-orbitron tracking-[0.2em] text-[0.65rem]">Scope</div>
                <div className="mt-1 text-spike-text font-mono">{shopify.scope || "—"}</div>
              </div>
              <div className="bg-black/40 border border-white/10 rounded-md p-3">
                <div className="text-spike-dim uppercase font-orbitron tracking-[0.2em] text-[0.65rem]">Customers synced</div>
                <div className="mt-1 text-spike-text font-bold text-base">{shopify.synced_users}</div>
              </div>
            </div>

            {Array.isArray(shopify.recent) && shopify.recent.length > 0 && (
              <div className="mt-4">
                <div className="text-[0.65rem] text-spike-dim font-orbitron uppercase tracking-[0.2em] mb-2">Recent sync attempts</div>
                <div className="bg-black/40 border border-white/10 rounded-md overflow-hidden divide-y divide-white/5" data-testid="shopify-sync-log">
                  {shopify.recent.slice(0, 5).map((r) => (
                    <div key={r.id} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                      <div className="font-mono truncate text-spike-text-muted">{r.user_id}</div>
                      <div className={`uppercase font-orbitron tracking-[0.15em] text-[0.65rem] ${r.status === "ok" ? "text-emerald-400" : r.status === "skipped" ? "text-spike-dim" : "text-red-400"}`}>{r.status}</div>
                      <div className="text-spike-dim">{new Date(r.at).toLocaleString("en-CA", { dateStyle: "short", timeStyle: "short" })}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={disconnectShopify}
              data-testid="shopify-disconnect-btn"
              className="mt-4 inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-xs font-orbitron uppercase tracking-[0.18em]"
            >
              Disconnect
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSettings;
